// Centralized API layer.
// Preview (USE_MOCK=true): mock data + browser (localStorage) persistence so the
// phone-lookup / returning-patient flow works with no backend.
// Production (USE_MOCK=false): calls the real CliniQueue backend endpoints below.

import axios from "axios";
import { API_BASE, API_ROOT } from "./appConfig";
import { normalizeSlotCollection } from "./slotUtils";

const http = axios.create({
  baseURL: API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`,
  timeout: 15000,
});

const httpRoot = axios.create({
  baseURL: API_ROOT,
  timeout: 15000,
});

const shouldRetryRequest = (error) => {
  return error?.response?.status === 404 || error?.response?.status === 502 || error?.code === "ERR_BAD_RESPONSE";
};

const requestAppointment = async (method, path, options = {}) => {
  let lastError;

  try {
    if (method === "get") {
      return await http.get(path, options);
    }
    if (method === "post") {
      return await http.post(path, options.data, { params: options.params });
    }
  } catch (error) {
    lastError = error;
    if (!shouldRetryRequest(error)) {
      throw error;
    }
  }

  const rootCandidates = path.startsWith("/api/")
    ? [path, path.slice(4)]
    : [path, `/api${path}`];

  for (const candidate of rootCandidates) {
    try {
      if (method === "get") {
        return await httpRoot.get(candidate, options);
      }
      if (method === "post") {
        return await httpRoot.post(candidate, options.data, { params: options.params });
      }
    } catch (error) {
      lastError = error;
      if (!shouldRetryRequest(error)) {
        throw error;
      }
    }
  }

  throw lastError;
};

/* ---------- locations ---------- */
export async function getStates() {
  const { data } = await http.get(`/locations/states`);
  return data;
}
export async function getCities(state) {
  const encodedState = encodeURIComponent(state || "");
  const { data } = await http.get(`/locations/cities/${encodedState}`);
  return data;
}

/* ---------- providers ---------- */
export async function searchClinics(state, city, q = "") {
  const { data } = await http.get(`/search/clinics`, { params: { state, city, q } });
  return data;
}
export async function searchDoctors(state, city, q = "") {
  const { data } = await http.get(`/search/doctors`, { params: { state, city, q } });
  const results = Array.isArray(data) ? data : [];
  return results.map((item) => ({
    ...item,
    id: item.doctor_id ?? item.id,
    doctorId: item.doctor_id ?? item.doctorId,
    clinicId: item.clinic_id ?? item.clinicId,
    doctorName: item.doctor_name ?? item.doctorName,
    name: item.doctor_name ?? item.doctorName ?? item.name,
    clinicName: item.clinic_name ?? item.clinicName,
  }));
}

/* ---------- patients (phone lookup + save) ---------- */
export async function lookupPatient(phone) {
  if (!phone) return [];
  const { data } = await http.get(`/appointments/patients-by-phone`, { params: { phone } });
  return data;
}
export async function savePatient(patient) {
  const { data } = await http.post(`/patients`, patient);
  return data;
}

/* ---------- slots ---------- */
export async function getSlotAvailability(providerId, providerType, date) {
  const params = { date };
  if (providerId !== undefined && providerId !== null) {
    params.doctorId = providerId;
  }

  const { data } = await requestAppointment("get", "/slots", { params });
  const rawSlots = Array.isArray(data?.slots) ? data.slots : [];
  return {
    slots: normalizeSlotCollection(rawSlots),
    onLeave: data?.onLeave === true,
    leaveReason: typeof data?.leaveReason === "string" ? data.leaveReason.trim() : "",
  };
}

export async function getSlots(providerId, providerType, date) {
  const availability = await getSlotAvailability(providerId, providerType, date);
  return availability.slots;
}

export async function getDoctorsByClinicId(clinicId) {
  if (!clinicId) return [];
  const { data } = await http.get(`/doctors/by-clinic-id/${encodeURIComponent(clinicId)}`);
  return data;
}

export async function getDoctorPhoto(doctorId, clinicId) {
  if (!doctorId || !clinicId) return null;
  try {
    const { data } = await http.get(`/doctors/${encodeURIComponent(doctorId)}/photo/content`, {
      headers: { "X-Clinic-Id": String(clinicId) },
      responseType: "blob",
      withCredentials: true,
    });
    return data;
  } catch {
    return null;
  }
}

/* ---------- appointments ---------- */
export async function confirmBooking(payload) {
  const { data } = await requestAppointment("post", "/appointments/book", { data: payload });
  return data;
}

export async function sendBookingOtp(phone, clinicId) {
  const { data } = await requestAppointment("post", "/appointments/send-booking-otp", { data: { phone, clinicId: String(clinicId || "") } });
  return data;
}

export async function verifyBookingOtp(phone, otp) {
  const { data } = await requestAppointment("post", "/appointments/verify-booking-otp", { data: { phone, otp } });
  return data;
}

export async function resendBookingOtp(phone, clinicId) {
  const { data } = await requestAppointment("post", "/appointments/send-booking-otp", { data: { phone, clinicId: String(clinicId || "") } });
  return data;
}

export async function getMyBooking(phone, date) {
  try {
    const { data } = await requestAppointment("get", "/appointments/my-booking", { params: { phone, date } });
    return normalizeBooking(data);
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

function normalizeSingleBooking(bookingData) {
  if (!bookingData) return null;

  return {
    ...bookingData,
    appointmentId: bookingData.appointmentId ?? bookingData.id,
    date: bookingData.date ?? bookingData.appointmentDate,
    slot: bookingData.slot ?? (typeof bookingData.slotTime === "string" ? bookingData.slotTime.slice(0, 5) : bookingData.slotTime),
    token: bookingData.token ?? bookingData.tokenNo,
    providerType: bookingData.providerType ?? "doctor",
    providerId: bookingData.providerId ?? bookingData.doctorId,
  };
}

export function normalizeBooking(data) {
  const bookingList = Array.isArray(data) ? data : data ? [data] : [];
  const normalized = bookingList.map(normalizeSingleBooking).filter(Boolean);

  if (normalized.length === 0) return null;
  if (normalized.length === 1) return normalized[0];
  return normalized;
}

export async function getPatientsByPhone(phone, date, status) {
  const { data } = await http.get(`/appointments/patients-by-phone`, { params: { phone, date, status } });
  return data;
}

export async function getLiveQueue(scheduleId = "sch_today") {
  const { data } = await http.get(`/appointments/live-queue`, { params: { scheduleId } });
  const current = data?.current || null;
  const next = data?.next || null;
  const waiting = Array.isArray(data?.waiting) ? data.waiting : [];

  return {
    ...data,
    current,
    next,
    waiting,
    nowServing: current?.tokenNo ?? null,
    totalInQueue: (current ? 1 : 0) + (next ? 1 : 0) + waiting.length,
    averageMinutesPerPatient: data?.averageConsultMinutes ?? data?.avgConsultMinutes ?? 5,
    estimatedTotalWaitMinutes: data?.estimatedTotalWaitMinutes ?? (waiting.length * (data?.averageConsultMinutes ?? data?.avgConsultMinutes ?? 5)),
  };
}

export async function cancelAppointment(appointmentId, otp) {
  const { data } = await requestAppointment("post", `/appointments/cancel/${appointmentId}`, { data: { otp } });
  return data;
}

export async function rescheduleAppointment(appointmentId, date, slot, otp) {
  const { data } = await requestAppointment("post", `/appointments/reschedule`, { params: { appointmentId, date, slot, otp } });
  return data;
}

export async function signInToPrescriptionPortal(phone, password) {
  const { data } = await http.post(`/prescriptions/portal-sign-in`, { phone, password });
  return data;
}

export async function checkPrescriptionPortalPhone(phone) {
  const { data } = await http.post(`/prescriptions/portal-check-phone`, { phone });
  return data;
}

export async function setPrescriptionPortalPassword(phone, temporaryPassword, newPassword, confirmPassword) {
  const { data } = await http.post(`/prescriptions/portal-set-password`, { phone, temporaryPassword, newPassword, confirmPassword });
  return data;
}

export async function requestPortalPasswordResetOtp(phone) {
  const { data } = await http.post(`/prescriptions/portal-forgot-password/request-otp`, { phone });
  return data;
}

export async function verifyPortalPasswordResetOtp(phone, otp) {
  const { data } = await http.post(`/prescriptions/portal-forgot-password/verify-otp`, { phone, otp });
  return data;
}

export async function resetPortalPassword(phone, newPassword, confirmPassword) {
  const { data } = await http.post(`/prescriptions/portal-forgot-password/reset`, { phone, newPassword, confirmPassword });
  return data;
}

export async function getPatientPrescriptions(phone = "", patientId = "", password = "") {
  if (!phone) return [];

  const { data } = await http.get(`/prescriptions/patient/prescriptions`, { params: { phone, password, patientId: patientId || undefined } });
  return data;
}

export async function getPatientReports(phone, patientId = "", password = "") {
  const { data } = await http.get(`/clinical-documents/patient-reports`, { params: { phone, password, patientId: patientId || undefined } });
  return data;
}

export async function getPrescriptionById(id, phone = "", password = "") {
  const { data } = await http.get(`/prescriptions/${id}`, { params: { phone: phone || undefined, password: password || undefined } });
  return data;
}

export async function downloadPrescriptionFile(id, phone = "", password = "") {
  const resp = await http.get(`/prescriptions/${id}/download`, { params: { phone: phone || undefined, password: password || undefined }, responseType: "blob" });
  return resp.data; // Blob
}
